import React, { useState, useMemo } from 'react';
import { Search, Plus, FileText, ChevronLeft, ChevronRight, X, Phone, MessageSquare } from 'lucide-react';
import { Select, Drawer, message } from 'antd';

// Interface chuẩn hóa cho Lịch hẹn
interface Appointment {
  id: string;
  roomIndex: number; // 0: Tiểu phẫu 1, 1: Laser/RF, 2: Cấy tóc, 3: Ghế tiêm 1-2
  dayIndex: number; // 0: T2, 1: T3, 2: T4, 3: T5, 4: T6, 5: T7, 6: CN
  dayLabel: string; // "THỨ 2 · 24/8", "THỨ 3 · 25/8",...
  startTime: string;
  customer: string;
  service: string;
  statusText: string;
  topOffset: string;
  height: string;
  bgColor: string;
  phone?: string;
  address?: string;
  telesale?: string;
  appointmentTime?: string;
  price?: string;
  roomName?: string;
  actionText?: string;
  note?: string;
}

export default function AppointmentManagement() {
  const [viewMode, setViewMode] = useState<'Ngày' | 'Tuần' | 'Danh sách'>('Ngày');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // State bộ lọc
  const [statusFilter, setStatusFilter] = useState<string>('Tất cả');
  const [telesaleFilter, setTelesaleFilter] = useState<string>('Tất cả');
  const [serviceFilter, setServiceFilter] = useState<string>('Tất cả');
  const [roomFilter, setRoomFilter] = useState<string>('Tất cả');

  // State điều khiển mở Drawer "Đặt lịch nhanh"
  const [isQuickBookingOpen, setIsQuickBookingOpen] = useState<boolean>(false);

  // Form state cho Đặt lịch nhanh
  const [newBooking, setNewBooking] = useState({
    customer: '',
    phone: '',
    service: 'Cấy mỡ hai bên má',
    roomIndex: 0,
    startTime: '14:00',
  });

  // Custom class cho Antd Select trong Dark mode
  const customSelectPopupClass =
    'dark:[&_.ant-select-dropdown]:!bg-slate-800 dark:[&_.ant-select-item]:!text-slate-200 dark:[&_.ant-select-item-option-selected]:!bg-slate-700 dark:[&_.ant-select-item-option-selected]:!text-teal-400 dark:[&_.ant-select-item-option-active]:!bg-slate-700/60';

  // 1. Chỉ số KPI
  const kpiData = [
    { title: 'TỔNG LỊCH HẸN', value: '82', sub: 'toàn hệ thống', color: 'text-slate-900 dark:text-white' },
    { title: 'ĐÃ XÁC NHẬN', value: '25,6%', sub: '21 / 82 lịch', color: 'text-slate-900 dark:text-white' },
    { title: 'CHỜ THỰC HIỆN', value: '19', sub: 'cần xử lý trong ngày', color: 'text-slate-900 dark:text-white' },
    { title: 'TỶ LỆ HUỶ', value: '7,3%', sub: '6 / 82 lịch', color: 'text-slate-900 dark:text-white' },
    { title: 'NO-SHOW RATE', value: '—', sub: 'cần status_history', color: 'text-slate-400' },
    { title: 'CÔNG SUẤT PHÒNG', value: '—', sub: 'cần resource_id', color: 'text-slate-400' },
  ];

  // 2. Thống kê trạng thái
  const statusSummaryCards = [
    { id: 'overdue', count: 2, label: 'Quá giờ chưa đến', border: 'border-l-rose-500', text: 'text-rose-600 dark:text-rose-400' },
    { id: 'waiting', count: 1, label: 'Đang chờ tại cơ sở', border: 'border-l-teal-500', text: 'text-teal-600 dark:text-teal-400' },
    { id: 'upcoming', count: 1, label: 'Sắp đến trong 60 phút', border: 'border-l-blue-600', text: 'text-blue-600 dark:text-blue-400' },
    { id: 'confirm', count: 4, label: 'Cần gọi xác nhận', border: 'border-l-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  ];

  // 3. Danh sách phòng / máy
  const roomColumns = [
    { id: 'room-1', name: 'Phòng tiểu phẫu 1', capacity: '18%' },
    { id: 'room-2', name: 'Phòng laser / RF', capacity: '9%' },
    { id: 'room-3', name: 'Phòng cấy tóc', capacity: '9%' },
    { id: 'room-4', name: 'Ghế tiêm 1–2', capacity: '9%' },
  ];

  // Các ngày trong Tuần
  const weekDays = [
    { label: 'THỨ 2', date: '24', dayLabel: 'THỨ 2 · 24/8', dayIndex: 0 },
    { label: 'THỨ 3', date: '25', dayLabel: 'THỨ 3 · 25/8', dayIndex: 1 },
    { label: 'THỨ 4', date: '26', dayLabel: 'THỨ 4 · 26/8', dayIndex: 2 },
    { label: 'THỨ 5', date: '27', dayLabel: 'THỨ 5 · 27/8', dayIndex: 3 },
    { label: 'THỨ 6', date: '28', dayLabel: 'THỨ 6 · 28/8 · HÔM NAY', dayIndex: 4 },
    { label: 'THỨ 7', date: '29', dayLabel: 'THỨ 7 · 29/8', dayIndex: 5 },
    { label: 'CN', date: '30', dayLabel: 'CHỦ NHẬT · 30/8', dayIndex: 6 },
  ];

  // Mốc thời gian Lưới
  const timeSlots = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'];

  // 5. Danh sách Lịch hẹn (Đầy đủ dữ liệu chuẩn theo ảnh)
  const [appointments, setAppointments] = useState<Appointment[]>([
    // THỨ 2
    {
      id: 'w1',
      roomIndex: 0,
      dayIndex: 0,
      dayLabel: 'THỨ 2 · 24/8',
      startTime: '09:00',
      customer: 'Thuy An Tran',
      phone: 'chưa có SĐT',
      service: 'Thăm khám tư vấn',
      statusText: 'Đã thực hiện',
      topOffset: '48px',
      height: '48px',
      bgColor: 'bg-emerald-100 dark:bg-emerald-950/70 border-emerald-300 dark:border-emerald-700 text-emerald-900 dark:text-emerald-200',
      actionText: 'Đặt tái khám',
    },
    {
      id: 'w2',
      roomIndex: 0,
      dayIndex: 0,
      dayLabel: 'THỨ 2 · 24/8',
      startTime: '15:00',
      customer: 'Liem Tran',
      phone: 'chưa có SĐT',
      service: 'Tiêm Rejuran trẻ hoá mặt',
      statusText: 'Đã thực hiện',
      topOffset: '336px',
      height: '48px',
      bgColor: 'bg-emerald-100 dark:bg-emerald-950/70 border-emerald-300 dark:border-emerald-700 text-emerald-900 dark:text-emerald-200',
      actionText: 'Đặt tái khám',
    },
    // THỨ 3
    {
      id: 'w3',
      roomIndex: 0,
      dayIndex: 1,
      dayLabel: 'THỨ 3 · 25/8',
      startTime: '09:30',
      customer: 'Lê Nhân',
      phone: 'chưa có SĐT',
      service: 'Thăm khám tư vấn',
      statusText: 'Đã huỷ',
      topOffset: '72px',
      height: '48px',
      bgColor: 'bg-slate-200 dark:bg-slate-700 border-slate-300 text-slate-700 dark:text-slate-200',
      actionText: 'Mở lại',
    },
    {
      id: 'w4',
      roomIndex: 0,
      dayIndex: 1,
      dayLabel: 'THỨ 3 · 25/8',
      startTime: '11:30',
      customer: 'Ngọc Ánh',
      phone: 'chưa có SĐT',
      service: 'Karisma RH collagen',
      statusText: 'Đã thực hiện',
      topOffset: '168px',
      height: '48px',
      bgColor: 'bg-emerald-100 dark:bg-emerald-950/70 border-emerald-300 dark:border-emerald-700 text-emerald-900 dark:text-emerald-200',
      actionText: 'Đặt tái khám',
    },
    {
      id: 'w5',
      roomIndex: 0,
      dayIndex: 1,
      dayLabel: 'THỨ 3 · 25/8',
      startTime: '13:00',
      customer: 'To MyAnh',
      phone: 'chưa có SĐT',
      service: 'Nâng cơ Ultherapy',
      statusText: 'Đã thực hiện',
      topOffset: '240px',
      height: '48px',
      bgColor: 'bg-emerald-100 dark:bg-emerald-950/70 border-emerald-300 dark:border-emerald-700 text-emerald-900 dark:text-emerald-200',
      actionText: 'Đặt tái khám',
    },
    {
      id: 'w6',
      roomIndex: 0,
      dayIndex: 1,
      dayLabel: 'THỨ 3 · 25/8',
      startTime: '14:00',
      customer: 'Thanh Yen',
      phone: 'chưa có SĐT',
      service: 'Thăm khám tư vấn',
      statusText: 'Không đến',
      topOffset: '288px',
      height: '48px',
      bgColor: 'bg-rose-100 dark:bg-rose-950/60 border-rose-300 dark:border-rose-800 text-rose-900 dark:text-rose-200',
      actionText: 'Gọi lại',
    },
    // THỨ 4
    {
      id: 'w7',
      roomIndex: 0,
      dayIndex: 2,
      dayLabel: 'THỨ 4 · 26/8',
      startTime: '11:00',
      customer: 'Ivan',
      phone: 'chưa có SĐT',
      service: 'Cấy tóc tự thân',
      statusText: 'Đã xác nhận',
      topOffset: '144px',
      height: '48px',
      bgColor: 'bg-slate-200/80 dark:bg-slate-700/80 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100',
      actionText: 'Check-in',
    },
    // THỨ 5
    {
      id: 'w8',
      roomIndex: 0,
      dayIndex: 3,
      dayLabel: 'THỨ 5 · 27/8',
      startTime: '11:00',
      customer: 'Tran Pham',
      phone: '0903***828',
      address: 'Ninh Giang',
      service: 'Karisma RH collagen · #51516760',
      statusText: 'Đặt lịch mới',
      topOffset: '144px',
      height: '48px',
      bgColor: 'bg-amber-100/70 dark:bg-amber-950/50 border-amber-300 dark:border-amber-800 text-amber-900 dark:text-amber-200',
      price: '7.990.000 đ',
      actionText: 'Gọi xác nhận',
    },
    {
      id: 'w9',
      roomIndex: 0,
      dayIndex: 3,
      dayLabel: 'THỨ 5 · 27/8',
      startTime: '16:00',
      customer: 'Hanh On',
      phone: 'chưa có SĐT',
      service: 'Thermage FLX vùng mặt',
      statusText: 'Chờ thực hiện',
      topOffset: '384px',
      height: '48px',
      bgColor: 'bg-purple-100 dark:bg-purple-950/60 border-purple-300 dark:border-purple-800 text-purple-900 dark:text-purple-200',
      actionText: 'Bàn giao KTV',
    },
    // THỨ 6 (HÔM NAY)
    {
      id: 'g1',
      roomIndex: 2,
      dayIndex: 4,
      dayLabel: 'THỨ 6 · 28/8 · HÔM NAY',
      startTime: '13:00',
      customer: 'Tina Nguyen',
      phone: '1409***898',
      address: 'Quang',
      service: 'Cấy tóc tự thân',
      roomName: 'Phòng cấy tóc',
      statusText: 'Đã thực hiện',
      topOffset: '240px',
      height: '48px',
      bgColor: 'bg-emerald-100 dark:bg-emerald-950/70 border-emerald-300 dark:border-emerald-700 text-emerald-900 dark:text-emerald-200',
      price: '38.000.000 đ',
      actionText: 'Đặt tái khám',
      telesale: 'Bửu Châu',
      appointmentTime: '28/08 13:00',
    },
    {
      id: 'g2',
      roomIndex: 0,
      dayIndex: 4,
      dayLabel: 'THỨ 6 · 28/8 · HÔM NAY',
      startTime: '14:00',
      customer: 'Mỹ Dáng',
      phone: 'chưa có SĐT',
      address: 'Bửu Châu',
      service: 'Cấy mỡ hai bên má',
      roomName: 'Phòng tiểu phẫu 1',
      statusText: 'Đặt lịch mới',
      topOffset: '288px',
      height: '48px',
      bgColor: 'bg-amber-100/70 dark:bg-amber-950/50 border-amber-300 dark:border-amber-800 text-amber-900 dark:text-amber-200',
      actionText: 'Gọi xác nhận',
      telesale: 'Bửu Châu',
      appointmentTime: '28/08 14:00',
    },
    {
      id: 'g3',
      roomIndex: 1,
      dayIndex: 4,
      dayLabel: 'THỨ 6 · 28/8 · HÔM NAY',
      startTime: '14:00',
      customer: 'Vy Ro Cam',
      phone: 'chưa có SĐT',
      service: 'Thermage FLX vùng mắt',
      roomName: 'Phòng laser / RF',
      statusText: 'Chờ thực hiện',
      topOffset: '288px',
      height: '48px',
      bgColor: 'bg-purple-100 dark:bg-purple-950/60 border-purple-300 dark:border-purple-800 text-purple-900 dark:text-purple-200',
      actionText: 'Bàn giao KTV',
      telesale: 'Minh Thư',
      appointmentTime: '28/08 14:00',
    },
    {
      id: 'g4',
      roomIndex: 3,
      dayIndex: 4,
      dayLabel: 'THỨ 6 · 28/8 · HÔM NAY',
      startTime: '15:00',
      customer: 'Kim Tuyết',
      phone: '0934***952',
      address: 'Ninh Giang',
      service: 'Tiêm Rejuran trẻ hoá mắt',
      roomName: 'Ghế tiêm 1–2',
      statusText: 'Đã xác nhận',
      topOffset: '336px',
      height: '48px',
      bgColor: 'bg-slate-200/80 dark:bg-slate-700/80 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100',
      price: '4.990.000 đ',
      actionText: 'Check-in',
      telesale: 'Bửu Châu',
      appointmentTime: '28/08 15:00',
    },
    {
      id: 'g5',
      roomIndex: 0,
      dayIndex: 4,
      dayLabel: 'THỨ 6 · 28/8 · HÔM NAY',
      startTime: '17:00',
      customer: 'Tien Vo',
      phone: '0827***672',
      address: 'Quang',
      service: 'Nâng mũi sụn K-Nose',
      roomName: 'Phòng tiểu phẫu 1',
      statusText: 'Đặt lịch mới',
      topOffset: '432px',
      height: '48px',
      bgColor: 'bg-amber-100/70 dark:bg-amber-950/50 border-amber-300 dark:border-amber-800 text-amber-900 dark:text-amber-200',
      price: '12.700.000 đ',
      actionText: 'Gọi xác nhận',
      telesale: 'Bửu Châu',
      appointmentTime: '28/08 17:00',
    },
    // THỨ 7
    {
      id: 'w10',
      roomIndex: 0,
      dayIndex: 5,
      dayLabel: 'THỨ 7 · 29/8',
      startTime: '15:00',
      customer: 'Lưu Thị Tường Vi',
      phone: '0973***842',
      address: 'Trương Mỹ Hiệp',
      service: 'Tiêm Rejuran trẻ hoá mắt',
      statusText: 'Đã xác nhận',
      topOffset: '336px',
      height: '48px',
      bgColor: 'bg-slate-200/80 dark:bg-slate-700/80 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100',
      price: '4.990.000 đ',
      actionText: 'Check-in',
    },
  ]);

  // Khách hàng đang chọn
  const [selectedCustomer, setSelectedCustomer] = useState<Appointment | null>(null);

  // Hàm sinh Style & Color cho Badge Trạng Thái
  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'Đã thực hiện':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span> Đã thực hiện
          </span>
        );
      case 'Đặt lịch mới':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300 border border-amber-300/80 dark:border-amber-800">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-600"></span> Đặt lịch mới
          </span>
        );
      case 'Chờ thực hiện':
      case 'Khách đã đến':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-950/80 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-600"></span> Chờ thực hiện
          </span>
        );
      case 'Đã xác nhận':
      case 'Xác nhận':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-sky-100 text-sky-800 dark:bg-sky-950/80 dark:text-sky-300 border border-sky-200 dark:border-sky-800">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-600"></span> Đã xác nhận
          </span>
        );
      case 'Không đến':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 dark:bg-rose-950/80 dark:text-rose-300 border border-rose-200 dark:border-rose-800">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-600"></span> Không đến
          </span>
        );
      case 'Đã huỷ':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-500"></span> Đã huỷ
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-800">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span> {status}
          </span>
        );
    }
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'Đã thực hiện':
        return 'bg-emerald-100 dark:bg-emerald-950/70 border-emerald-300 dark:border-emerald-700 text-emerald-900 dark:text-emerald-200';
      case 'Đặt lịch mới':
        return 'bg-amber-100/70 dark:bg-amber-950/50 border-amber-300 dark:border-amber-800 text-amber-900 dark:text-amber-200';
      case 'Chờ thực hiện':
      case 'Khách đã đến':
        return 'bg-purple-100 dark:bg-purple-950/60 border-purple-300 dark:border-purple-800 text-purple-900 dark:text-purple-200';
      case 'Đã xác nhận':
      case 'Xác nhận':
        return 'bg-slate-200/80 dark:bg-slate-700/80 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100';
      case 'Không đến':
        return 'bg-rose-100 dark:bg-rose-950/60 border-rose-300 dark:border-rose-800 text-rose-900 dark:text-rose-200';
      case 'Đã huỷ':
        return 'bg-slate-200 dark:bg-slate-700 border-slate-300 text-slate-700 dark:text-slate-200';
      default:
        return 'bg-amber-100/70 dark:bg-amber-950/50 border-amber-300 dark:border-amber-800 text-amber-900 dark:text-amber-200';
    }
  };

  // Cập nhật Trạng thái cho Khách đang chọn
  const handleUpdateStatus = (newStatus: string) => {
    if (!selectedCustomer) return;

    const updatedBgColor = getStatusStyle(newStatus);

    setAppointments((prev) =>
      prev.map((item) =>
        item.id === selectedCustomer.id
          ? { ...item, statusText: newStatus, bgColor: updatedBgColor }
          : item
      )
    );

    setSelectedCustomer((prev) =>
      prev
        ? {
            ...prev,
            statusText: newStatus,
            bgColor: updatedBgColor,
          }
        : null
    );

    message.success(`Đã chuyển trạng thái sang: "${newStatus}"`);
  };

  // Xử lý dời lịch hẹn khi click nút "+ chèn HH:mm"
  const handleMoveAppointment = (newTime: string) => {
    if (!selectedCustomer) return;

    const slotIndex = timeSlots.indexOf(newTime);
    if (slotIndex === -1) return;

    const newTopOffset = `${slotIndex * 48}px`;

    setAppointments((prev) =>
      prev.map((item) =>
        item.id === selectedCustomer.id
          ? { ...item, startTime: newTime, topOffset: newTopOffset }
          : item
      )
    );

    setSelectedCustomer((prev) =>
      prev
        ? {
            ...prev,
            startTime: newTime,
            topOffset: newTopOffset,
          }
        : null
    );

    message.success(`Đã dời lịch sang khung giờ ${newTime}`);
  };

  // Xử lý Đặt lịch nhanh
  const handleCreateQuickBooking = () => {
    if (!newBooking.customer.trim()) {
      message.error('Vui lòng nhập tên khách hàng!');
      return;
    }

    const slotIndex = timeSlots.indexOf(newBooking.startTime);
    const topOffset = `${(slotIndex !== -1 ? slotIndex : 6) * 48}px`;

    const newAppt: Appointment = {
      id: `g_${Date.now()}`,
      roomIndex: Number(newBooking.roomIndex),
      dayIndex: 4, // Mặc định Thứ 6
      dayLabel: 'THỨ 6 · 28/8 · HÔM NAY',
      startTime: newBooking.startTime,
      customer: newBooking.customer,
      service: newBooking.service || 'Chưa chọn dịch vụ',
      statusText: 'Đặt lịch mới',
      topOffset,
      height: '48px',
      bgColor: getStatusStyle('Đặt lịch mới'),
      phone: newBooking.phone || 'chưa có SĐT',
      telesale: 'Bửu Châu',
      appointmentTime: `28/08 ${newBooking.startTime}`,
      actionText: 'Gọi xác nhận',
    };

    setAppointments((prev) => [...prev, newAppt]);
    setSelectedCustomer(newAppt);
    setIsQuickBookingOpen(false);
    setNewBooking({
      customer: '',
      phone: '',
      service: 'Cấy mỡ hai bên má',
      roomIndex: 0,
      startTime: '14:00',
    });

    message.success('Đã thêm đặt lịch thành công!');
  };

  // Lọc lịch hẹn theo từ khóa & các bộ lọc
  const filteredAppointments = useMemo(() => {
    return appointments.filter((item) => {
      const matchSearch =
        searchQuery.trim() === '' ||
        item.customer.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.service.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.phone && item.phone.includes(searchQuery));

      const matchStatus = statusFilter === 'Tất cả' || item.statusText === statusFilter;
      const matchTelesale = telesaleFilter === 'Tất cả' || item.telesale === telesaleFilter;
      const matchService = serviceFilter === 'Tất cả' || item.service === serviceFilter;
      const matchRoom = roomFilter === 'Tất cả' || roomColumns[item.roomIndex]?.name === roomFilter;

      return matchSearch && matchStatus && matchTelesale && matchService && matchRoom;
    });
  }, [appointments, searchQuery, statusFilter, telesaleFilter, serviceFilter, roomFilter]);

  // Gom nhóm danh sách lịch hẹn theo Thứ cho View "Danh sách"
  const groupedAppointments = useMemo(() => {
    const groups: { [key: string]: Appointment[] } = {};
    weekDays.forEach((day) => {
      groups[day.dayLabel] = filteredAppointments.filter((a) => a.dayIndex === day.dayIndex);
    });
    return groups;
  }, [filteredAppointments]);

  return (
    <div className="p-4 sm:p-6 bg-slate-100 dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 space-y-4 transition-colors duration-200">
      
      {/* 1. Header */}
      <div>
        <h1 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">Lịch tư vấn – màn vận hành</h1>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-5xl leading-relaxed">
          Lễ tân làm theo danh sách việc ở cột trái — gọi ai trước, ai đang chờ, ai quá giờ. Điều phối làm theo lưới phòng và máy — khung nào trống, chèn lịch nào vào đâu. Bấm một việc ở cột trái hoặc một lịch trên lưới để chọn; thông tin khách sẽ hiển thị ở cột bên phải.
        </p>
      </div>

      {/* 2. Chỉ số Quản lý */}
      <div>
        <div className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-2">
          <span>CHỈ SỐ QUẢN LÝ</span>
          <span className="font-normal text-slate-400">(Toàn hệ thống, không phải dữ liệu ca trực trong ngày)</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {kpiData.map((kpi, idx) => (
            <div key={idx} className="bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700 shadow-2xs">
              <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase truncate">{kpi.title}</div>
              <div className={`text-xl font-black mt-1 ${kpi.color}`}>{kpi.value}</div>
              <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 truncate">{kpi.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 3. Ca trực & Thống kê */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
        <div className="lg:col-span-4 bg-white dark:bg-slate-800 p-3.5 rounded-lg border border-slate-200 dark:border-slate-700 shadow-2xs flex items-center gap-4">
          <div className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">14:20</div>
          <div className="text-xs border-l border-slate-200 dark:border-slate-700 pl-3">
            <div className="font-bold text-slate-700 dark:text-slate-200">Thứ 6 - 28/08/2026</div>
            <div className="text-slate-400 dark:text-slate-400 text-[11px] mt-0.5">
              Ca trực: <span className="font-medium text-slate-600 dark:text-slate-300">Hồ Nhi · Trần Hưng Đạo Q1</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {statusSummaryCards.map((card) => (
            <div
              key={card.id}
              className={`bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700 shadow-2xs border-l-4 ${card.border} flex items-center gap-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors`}
            >
              <span className={`text-2xl font-black ${card.text}`}>{card.count}</span>
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 leading-tight">{card.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 4. Filter Bar */}
      <div className="flex flex-col xl:flex-row items-stretch xl:items-center justify-between gap-3 pt-1">
        <div className="flex items-center flex-wrap gap-2">
          <div className="relative w-full sm:w-auto">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Mã lịch hẹn, tên khách, SĐT"
              className="pl-8 pr-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md text-xs focus:outline-none focus:border-teal-500 w-full sm:w-56 shadow-2xs placeholder:text-slate-400 text-slate-800 dark:text-slate-200 transition-colors"
            />
          </div>

          <div className="inline-flex items-center border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 px-2.5 py-1 text-xs shadow-2xs">
            <span className="text-slate-400 mr-1 whitespace-nowrap">Trạng thái:</span>
            <Select
              value={statusFilter}
              onChange={(val) => setStatusFilter(val)}
              variant="borderless"
              size="small"
              popupClassName={customSelectPopupClass}
              options={[
                { value: 'Tất cả', label: 'Tất cả' },
                { value: 'Đặt lịch mới', label: 'Đặt lịch mới' },
                { value: 'Đã xác nhận', label: 'Đã xác nhận' },
                { value: 'Chờ thực hiện', label: 'Chờ thực hiện' },
                { value: 'Đã thực hiện', label: 'Đã thực hiện' },
              ]}
            />
          </div>

          <div className="inline-flex items-center border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 px-2.5 py-1 text-xs shadow-2xs">
            <span className="text-slate-400 mr-1 whitespace-nowrap">Telesale:</span>
            <Select
              value={telesaleFilter}
              onChange={(val) => setTelesaleFilter(val)}
              variant="borderless"
              size="small"
              popupClassName={customSelectPopupClass}
              options={[
                { value: 'Tất cả', label: 'Tất cả' },
                { value: 'Bửu Châu', label: 'Bửu Châu' },
                { value: 'Minh Thư', label: 'Minh Thư' },
              ]}
            />
          </div>

          <div className="inline-flex items-center border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 px-2.5 py-1 text-xs shadow-2xs">
            <span className="text-slate-400 mr-1 whitespace-nowrap">Dịch vụ:</span>
            <Select
              value={serviceFilter}
              onChange={(val) => setServiceFilter(val)}
              variant="borderless"
              size="small"
              popupClassName={customSelectPopupClass}
              options={[
                { value: 'Tất cả', label: 'Tất cả' },
                { value: 'Cấy tóc tự thân', label: 'Cấy tóc tự thân' },
                { value: 'Cấy mỡ hai bên má', label: 'Cấy mỡ hai bên má' },
                { value: 'Thermage FLX vùng mắt', label: 'Thermage FLX vùng mắt' },
                { value: 'Tiêm Rejuran trẻ hoá mắt', label: 'Tiêm Rejuran trẻ hoá mắt' },
                { value: 'Nâng mũi sụn K-Nose', label: 'Nâng mũi sụn K-Nose' },
              ]}
            />
          </div>

          <div className="inline-flex items-center border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 px-2.5 py-1 text-xs shadow-2xs gap-1">
            <span className="text-slate-400 whitespace-nowrap">Phòng / máy:</span>
            <Select
              value={roomFilter}
              onChange={(val) => setRoomFilter(val)}
              variant="borderless"
              size="small"
              popupClassName={customSelectPopupClass}
              options={[
                { value: 'Tất cả', label: 'Tất cả' },
                ...roomColumns.map((r) => ({ value: r.name, label: r.name })),
              ]}
            />
            <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-1.5 py-0.2 rounded">Mới</span>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start xl:self-auto">
          <div className="bg-slate-200/70 dark:bg-slate-800 p-0.5 rounded-md flex gap-0.5 border border-slate-200 dark:border-slate-700">
            {(['Ngày', 'Tuần', 'Danh sách'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1 rounded text-xs transition-all ${
                  viewMode === mode
                    ? 'bg-white dark:bg-slate-700 font-bold text-slate-800 dark:text-white shadow-2xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>

          <button
            onClick={() => message.info('Tính năng xuất Excel đang được xử lý')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-medium rounded-md border border-slate-200 dark:border-slate-700 text-xs shadow-2xs transition-colors"
          >
            <FileText className="w-3.5 h-3.5 text-slate-500" />
            Xuất Excel
          </button>

          <button
            onClick={() => setIsQuickBookingOpen(true)}
            className="flex items-center gap-1.5 px-3.5 py-1.5 bg-teal-800 hover:bg-teal-900 dark:bg-teal-700 dark:hover:bg-teal-600 text-white font-semibold rounded-md text-xs transition-colors shadow-2xs"
          >
            <Plus className="w-3.5 h-3.5" />Đặt lịch nhanh
          </button>
        </div>
      </div>

      {/* 5. Khối Nội dung chính 3 Cột (Hàng chờ | Nội dung Lịch | Chi tiết khách hàng) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 pt-1 items-start">
        
        {/* CỘT 1: HÀNG CHỜ XỬ LÝ (LỄ TÂN) */}
        <div className="lg:col-span-3 space-y-3">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3 shadow-2xs space-y-3">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-2">
              <div>
                <h2 className="font-bold text-sm text-slate-900 dark:text-white">Hàng chờ xử lý</h2>
                <p className="text-[11px] text-slate-400">8 việc · xếp theo mức gấp</p>
              </div>
              <span className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-[10px] font-bold px-2 py-0.5 rounded border border-slate-200 dark:border-slate-600">
                LỄ TÂN
              </span>
            </div>

            {/* Việc tiếp theo Highlight Box */}
            <div className="bg-rose-50/80 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 rounded-md p-2.5 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-rose-700 dark:text-rose-400 tracking-wider">
                  VIỆC TIẾP THEO
                </span>
                <span className="bg-rose-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                  QUÁ GIỜ CHƯA ĐẾN
                </span>
              </div>
              <div>
                <div className="font-bold text-sm text-slate-900 dark:text-white">Mỹ Dáng</div>
                <div className="text-xs text-slate-600 dark:text-slate-300">Cấy mỡ hai bên má</div>
              </div>
              <div className="bg-rose-100 dark:bg-rose-900/50 text-rose-800 dark:text-rose-300 text-[11px] font-semibold px-2 py-0.5 rounded inline-block">
                20 phút
              </div>
              <div className="pt-1">
                <button
                  onClick={() => {
                    const found = appointments.find((a) => a.customer === 'Mỹ Dáng');
                    if (found) setSelectedCustomer(found);
                    message.info('Đã chọn khách hàng Mỹ Dáng để gọi.');
                  }}
                  className="w-full py-1.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 font-bold text-slate-800 dark:text-slate-100 rounded text-xs shadow-2xs transition-colors"
                >
                  Gọi khách
                </button>
              </div>
              <div className="flex items-center justify-between text-[11px] text-slate-500 pt-0.5">
                <button className="underline hover:text-slate-800 dark:hover:text-slate-300">Bỏ qua việc này</button>
                <span>còn 7 việc</span>
              </div>
            </div>

            {/* Danh sách các việc trong hàng chờ */}
            <div className="space-y-3 pt-1">
              
              {/* 1. QUÁ GIỜ CHƯA ĐẾN */}
              <div className="space-y-2">
                <div className="text-[11px] font-bold text-rose-600 dark:text-rose-400 uppercase flex justify-between items-center">
                  <span>QUÁ GIỜ CHƯA ĐẾN</span>
                  <span>2</span>
                </div>
                
                {/* Item Mỹ Dáng */}
                <div
                  onClick={() => {
                    const found = appointments.find((a) => a.customer === 'Mỹ Dáng');
                    if (found) setSelectedCustomer(found);
                  }}
                  className={`p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-md border border-l-4 border-l-rose-500 cursor-pointer transition-all space-y-1.5 text-xs ${
                    selectedCustomer?.customer === 'Mỹ Dáng'
                      ? 'border-rose-500 ring-1 ring-rose-500/50 bg-rose-50/20'
                      : 'border-slate-200 dark:border-slate-700'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <span className="font-bold text-slate-900 dark:text-white">Mỹ Dáng</span>
                    <span className="text-[10px] text-rose-600 dark:text-rose-400 font-medium">quá 20'</span>
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 text-[11px]">Cấy mỡ hai bên má</p>
                  <div className="flex justify-between items-center pt-1">
                    <button className="px-2.5 py-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded font-medium text-[11px] hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
                      Gọi khách
                    </button>
                    <span className="text-[10px] text-slate-400 font-mono">20 phút</span>
                  </div>
                </div>

                {/* Item Tran Pham */}
                <div className="p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-md border border-l-4 border-l-rose-500 border-slate-200 dark:border-slate-700 space-y-1.5 text-xs">
                  <div className="flex justify-between items-start">
                    <span className="font-bold text-slate-900 dark:text-white">Tran Pham</span>
                    <span className="text-[10px] text-rose-600 dark:text-rose-400 font-medium">quá 2 ngày</span>
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 text-[11px]">Karisma RH collagen</p>
                  <div className="flex justify-between items-center pt-1">
                    <button className="px-2.5 py-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded font-medium text-[11px] hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
                      Gọi khách
                    </button>
                    <span className="text-[10px] text-slate-400 font-mono">27/8 11:00</span>
                  </div>
                </div>
              </div>

              {/* 2. ĐANG CHỜ TẠI CƠ SỞ */}
              <div className="space-y-2">
                <div className="text-[11px] font-bold text-teal-600 dark:text-teal-400 uppercase flex justify-between items-center">
                  <span>ĐANG CHỜ TẠI CƠ SỞ</span>
                  <span>1</span>
                </div>
                <div
                  onClick={() => {
                    const found = appointments.find((a) => a.customer === 'Vy Ro Cam');
                    if (found) setSelectedCustomer(found);
                  }}
                  className={`p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-md border border-l-4 border-l-teal-500 cursor-pointer transition-all space-y-1.5 text-xs ${
                    selectedCustomer?.customer === 'Vy Ro Cam'
                      ? 'border-teal-500 ring-1 ring-teal-500/50 bg-teal-50/20'
                      : 'border-slate-200 dark:border-slate-700'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <span className="font-bold text-slate-900 dark:text-white">Vy Ro Cam</span>
                    <span className="text-[10px] text-teal-600 dark:text-teal-400 font-medium">chờ 28'</span>
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 text-[11px]">Thermage FLX vùng mắt</p>
                  <div className="flex justify-between items-center pt-1">
                    <button className="px-2.5 py-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded font-medium text-[11px] hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
                      Bàn giao KTV
                    </button>
                    <span className="text-[10px] text-slate-400 font-mono">đến 13:52</span>
                  </div>
                </div>
              </div>

              {/* 3. SẮP ĐẾN TRONG 60 PHÚT */}
              <div className="space-y-2">
                <div className="text-[11px] font-bold text-blue-600 dark:text-blue-400 uppercase flex justify-between items-center">
                  <span>SẮP ĐẾN TRONG 60 PHÚT</span>
                  <span>1</span>
                </div>
                <div
                  onClick={() => {
                    const found = appointments.find((a) => a.customer === 'Kim Tuyết');
                    if (found) setSelectedCustomer(found);
                  }}
                  className={`p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-md border border-l-4 border-l-blue-600 cursor-pointer transition-all space-y-1.5 text-xs ${
                    selectedCustomer?.customer === 'Kim Tuyết'
                      ? 'border-blue-500 ring-1 ring-blue-500/50 bg-blue-50/20'
                      : 'border-slate-200 dark:border-slate-700'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <span className="font-bold text-slate-900 dark:text-white">Kim Tuyết</span>
                    <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">còn 40'</span>
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 text-[11px]">Tiêm Rejuran trẻ hoá mắt</p>
                  <div className="flex justify-between items-center pt-1">
                    <button className="px-2.5 py-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded font-medium text-[11px] hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
                      Check-in
                    </button>
                    <span className="text-[10px] text-slate-400 font-mono">15:00</span>
                  </div>
                </div>
              </div>

              {/* 4. CẦN GỌI XÁC NHẬN */}
              <div className="space-y-2">
                <div className="text-[11px] font-bold text-amber-600 dark:text-amber-400 uppercase flex justify-between items-center">
                  <span>CẦN GỌI XÁC NHẬN</span>
                  <span>4</span>
                </div>

                {/* Tien Vo */}
                <div
                  onClick={() => {
                    const found = appointments.find((a) => a.customer === 'Tien Vo');
                    if (found) setSelectedCustomer(found);
                  }}
                  className={`p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-md border border-l-4 border-l-amber-500 cursor-pointer transition-all space-y-1.5 text-xs ${
                    selectedCustomer?.customer === 'Tien Vo'
                      ? 'border-amber-500 ring-1 ring-amber-500/50 bg-amber-50/20'
                      : 'border-slate-200 dark:border-slate-700'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <span className="font-bold text-slate-900 dark:text-white">Tien Vo</span>
                    <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">hôm nay</span>
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 text-[11px]">Nâng mũi sụn K-Nose</p>
                  <div className="flex justify-between items-center pt-1">
                    <button className="px-2.5 py-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded font-medium text-[11px] hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
                      Gọi xác nhận
                    </button>
                    <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono">12.700.000</span>
                  </div>
                </div>

                {/* Katie Katie */}
                <div className="p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-md border border-l-4 border-l-amber-500 border-slate-200 dark:border-slate-700 space-y-1.5 text-xs">
                  <div className="flex justify-between items-start">
                    <span className="font-bold text-slate-900 dark:text-white">Katie Katie</span>
                    <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">5/9</span>
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 text-[11px]">Nâng cơ trẻ hóa đa tầng Ultherapy</p>
                  <div className="flex justify-between items-center pt-1">
                    <button className="px-2.5 py-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded font-medium text-[11px] hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
                      Xin số điện thoại
                    </button>
                    <span className="text-[10px] text-slate-400 font-mono">chưa có SĐT</span>
                  </div>
                </div>

                {/* Quinn Le */}
                <div className="p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-md border border-l-4 border-l-amber-500 border-slate-200 dark:border-slate-700 space-y-1.5 text-xs">
                  <div className="flex justify-between items-start">
                    <span className="font-bold text-slate-900 dark:text-white">Quinn Le</span>
                    <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">10/9</span>
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 text-[11px]">Ultherapy Prime + Thermage FLX</p>
                  <div className="flex justify-between items-center pt-1">
                    <button className="px-2.5 py-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded font-medium text-[11px] hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
                      Gọi xác nhận
                    </button>
                    <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono">45.000.000</span>
                  </div>
                </div>

                {/* Phạm Thị Thuý Vi */}
                <div className="p-2.5 bg-slate-50 dark:bg-slate-800/80 rounded-md border border-l-4 border-l-amber-500 border-slate-200 dark:border-slate-700 space-y-1.5 text-xs">
                  <div className="flex justify-between items-start">
                    <span className="font-bold text-slate-900 dark:text-white">Phạm Thị Thuý Vi</span>
                    <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">12/9</span>
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 text-[11px]">Căng da mặt toàn phần nội soi</p>
                  <div className="flex justify-between items-center pt-1">
                    <button className="px-2.5 py-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded font-medium text-[11px] hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
                      Gửi nhắc 48h
                    </button>
                    <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono">85.500.000</span>
                  </div>
                </div>

              </div>

            </div>
          </div>
        </div>

        {/* CỘT 2: HIỂN THỊ NỘI DUNG (KHI BẤM "DANH SÁCH" SẼ RA BẢNG THEO HÌNH ẢNH) */}
        <div className={`${selectedCustomer ? 'lg:col-span-6 xl:col-span-6' : 'lg:col-span-9 xl:col-span-9'} transition-all duration-200`}>
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden shadow-2xs">
            
            {/* Control Bar Lưới & Danh Sách */}
            <div className="p-2.5 bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 flex flex-col md:flex-row md:items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-1.5">
                <button className="p-1 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700">
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <button className="p-1 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700">
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
                <button className="px-2.5 py-1 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 font-medium">
                  Hôm nay
                </button>
                
                <div className="flex items-center gap-2 ml-2">
                  <span className="font-bold text-slate-900 dark:text-white text-sm">
                    Danh sách lịch hẹn · tuần 24/8 – 30/8
                  </span>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    15 lịch hẹn · chọn nhiều dòng để thao tác hàng loạt
                  </span>
                </div>
              </div>

              <button className="self-start md:self-auto bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 px-2.5 py-1 rounded-full text-[11px] font-bold flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-600"></span> Điều phối
              </button>
            </div>

            {/* Chú thích Trạng Thái */}
            <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700/60 bg-white dark:bg-slate-800 flex items-center gap-3 text-[11px] flex-wrap">
              <span className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
                <span className="w-2 h-2 rounded bg-amber-400"></span> Đặt lịch mới
              </span>
              <span className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
                <span className="w-2 h-2 rounded bg-sky-500"></span> Đã xác nhận
              </span>
              <span className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
                <span className="w-2 h-2 rounded bg-purple-500 whitespace-nowrap"></span> Chờ thực hiện
              </span>
              <span className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
                <span className="w-2 h-2 rounded bg-emerald-500"></span> Đã thực hiện
              </span>
              <span className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
                <span className="w-2 h-2 rounded bg-rose-500"></span> Không đến
              </span>
              <span className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
                <span className="w-2 h-2 rounded bg-slate-400"></span> Đã huỷ
              </span>
            </div>

            {/* CHẾ ĐỘ 1: VIEW DANH SÁCH (HÌNH ẢNH MỚI CỦA BẠN) */}
            {viewMode === 'Danh sách' && (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/80 text-slate-400 font-bold uppercase text-[10px]">
                      <th className="p-3 w-10 text-center">
                        <input type="checkbox" className="rounded border-slate-300" />
                      </th>
                      <th className="p-3 w-16">GIỜ</th>
                      <th className="p-3 w-32">TRẠNG THÁI</th>
                      <th className="p-3">KHÁCH HÀNG</th>
                      <th className="p-3">DỊCH VỤ</th>
                      <th className="p-3">PHÒNG / MÁY</th>
                      <th className="p-3 text-right">GIÁ TRỊ ĐƠN</th>
                      <th className="p-3 text-center">HÀNH ĐỘNG</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                    {Object.entries(groupedAppointments).map(([dayLabel, list]) => (
                      <React.Fragment key={dayLabel}>
                        {/* Dòng Tiêu Đề Nhóm Ngày */}
                        <tr className="bg-slate-50/80 dark:bg-slate-800/50 font-bold text-slate-600 dark:text-slate-300">
                          <td colSpan={7} className="px-3 py-2 text-[11px] uppercase tracking-wider">
                            {dayLabel}
                          </td>
                          <td className="px-3 py-2 text-right text-[10px] text-slate-400 font-bold tracking-wider">
                            {list.length} LỊCH
                          </td>
                        </tr>

                        {/* Các dòng Lịch Hẹn */}
                        {list.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="p-3 text-center text-slate-400 text-xs italic">
                              Không có lịch hẹn
                            </td>
                          </tr>
                        ) : (
                          list.map((item) => {
                            const isSelected = selectedCustomer?.id === item.id;
                            return (
                              <tr
                                key={item.id}
                                onClick={() => setSelectedCustomer(item)}
                                className={`hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors cursor-pointer ${
                                  isSelected ? 'bg-teal-50/40 dark:bg-teal-950/20' : ''
                                }`}
                              >
                                <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                                  <input type="checkbox" className="rounded border-slate-300" />
                                </td>
                                <td className="p-3 font-bold font-mono text-slate-800 dark:text-slate-200">
                                  {item.startTime}
                                </td>
                                <td className="p-3 whitespace-nowrap">{renderStatusBadge(item.statusText)}</td>
                                <td className="p-3">
                                  <div className="font-bold text-slate-900 dark:text-white">{item.customer}</div>
                                  <div className="text-[11px] text-slate-400">
                                    {item.phone} {item.address && `· ${item.address}`}
                                  </div>
                                </td>
                                <td className="p-3 text-slate-700 dark:text-slate-300">{item.service}</td>
                                <td className="p-3 text-slate-400">{item.roomName || '—'}</td>
                                <td className="p-3 text-right font-medium text-slate-800 dark:text-slate-200">
                                  {item.price || '—'}
                                </td>
                                <td className="p-3 text-center">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      message.info(`Đã bấm ${item.actionText || 'xử lý'} cho ${item.customer}`);
                                    }}
                                    className="px-3 py-1 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 rounded font-medium text-xs text-slate-800 dark:text-slate-100 shadow-2xs transition-colors"
                                  >
                                    {item.actionText || 'Xem'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* CHẾ ĐỘ 2 & 3: VIEW LƯỚI NGÀY / TUẦN */}
            {viewMode !== 'Danh sách' && (
              <div className="relative overflow-x-auto min-w-[600px]">
                {/* Header Cột Lưới */}
                {viewMode === 'Tuần' ? (
                  <div className="grid grid-cols-[10%_repeat(7,minmax(0,1fr))] border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/80 text-center py-2">
                    <div className="text-left pl-2.5 font-bold text-slate-400 text-[11px]">GIỜ</div>
                    {weekDays.map((w) => (
                      <div key={w.dayIndex} className="text-center px-0.5 min-w-0">
                        <div className="font-bold text-slate-500 dark:text-slate-400 text-[10px] uppercase">{w.label}</div>
                        <div className={`text-base font-black leading-tight ${w.date === '28' ? 'text-teal-600 dark:text-teal-400' : 'text-slate-800 dark:text-slate-200'}`}>
                          {w.date}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-[16.666%_repeat(4,minmax(0,1fr))] border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/80 text-center py-2">
                    <div className="text-left pl-2.5 font-bold text-slate-400 text-[11px]">GIỜ</div>
                    {roomColumns.map((col) => (
                      <div key={col.id} className="text-center px-0.5 min-w-0">
                        <div className="font-bold text-slate-800 dark:text-slate-200 text-[11px] truncate">{col.name}</div>
                        <div className="text-[9px] text-slate-400">công suất {col.capacity}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Ô Thời Gian & Lưới */}
                <div className="relative divide-y divide-slate-100 dark:divide-slate-700/60">
                  <div className="absolute left-0 right-0 z-20 flex items-center pointer-events-none" style={{ top: '304px' }}>
                    <span className="bg-rose-600 text-white text-[9px] font-bold px-1 rounded-r shrink-0">14:20</span>
                    <div className="w-full border-t-2 border-rose-500"></div>
                  </div>

                  {timeSlots.map((time) => (
                    <div
                      key={time}
                      className={`grid ${
                        viewMode === 'Tuần'
                          ? 'grid-cols-[10%_repeat(7,minmax(0,1fr))]'
                          : 'grid-cols-[16.666%_repeat(4,minmax(0,1fr))]'
                      } min-h-[48px] items-stretch text-xs`}
                    >
                      <div className="p-1.5 text-slate-400 font-mono text-[10px] border-r border-slate-100 dark:border-slate-700/60 bg-slate-50/30 dark:bg-slate-800/30">
                        {time}
                      </div>

                      {viewMode === 'Tuần' ? (
                        weekDays.map((w) => (
                          <div key={w.dayIndex} className="border-r border-slate-100 dark:border-slate-700/60 p-0.5 relative min-w-0"></div>
                        ))
                      ) : (
                        roomColumns.map((col, roomIdx) => {
                          const hasApptInSlot = filteredAppointments.some(
                            (a) => a.roomIndex === roomIdx && a.startTime === time
                          );
                          const isSelectedRoom = selectedCustomer?.roomIndex === roomIdx;

                          return (
                            <div key={col.id} className="border-r border-slate-100 dark:border-slate-700/60 p-0.5 relative flex items-center justify-center min-w-0">
                              {isSelectedRoom && !hasApptInSlot && (
                                <button
                                  onClick={() => handleMoveAppointment(time)}
                                  className="w-full h-[38px] border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-teal-500 hover:bg-teal-50/50 text-slate-500 hover:text-teal-700 rounded font-mono text-[10px] transition-all flex items-center justify-center truncate"
                                >
                                  + chèn {time}
                                </button>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  ))}

                  {/* Thẻ Lịch hẹn trên Lưới */}
                  {filteredAppointments.map((appt) => {
                    const isSelected = selectedCustomer?.id === appt.id;
                    const colIdx = viewMode === 'Tuần' ? (appt.dayIndex ?? 4) : appt.roomIndex;
                    const totalCols = viewMode === 'Tuần' ? 7 : 4;
                    const timeColWidth = viewMode === 'Tuần' ? 10 : 16.666;
                    const colWidth = (100 - timeColWidth) / totalCols;

                    if (viewMode === 'Ngày' && appt.dayIndex !== undefined && appt.dayIndex !== 4) {
                      return null;
                    }

                    return (
                      <div
                        key={appt.id}
                        onClick={() => setSelectedCustomer(appt)}
                        style={{
                          top: appt.topOffset,
                          left: `calc(${timeColWidth}% + ${colIdx * colWidth}% + 2px)`,
                          width: `calc(${colWidth}% - 4px)`,
                          height: appt.height,
                        }}
                        className={`absolute z-10 p-1 rounded border text-[11px] shadow-2xs cursor-pointer transition-all hover:scale-[1.01] flex flex-col justify-center overflow-hidden whitespace-nowrap ${appt.bgColor} ${
                          isSelected ? 'ring-2 ring-rose-500 ring-offset-1 z-30 font-bold border-rose-500' : ''
                        }`}
                      >
                        <div className="text-[9px] font-mono leading-none opacity-80 mb-0.5 truncate">
                          {appt.startTime} · {appt.statusText}
                        </div>
                        <div className="font-bold truncate leading-tight">{appt.customer}</div>
                      </div>
                    );
                  })}

                </div>
              </div>
            )}

          </div>
        </div>

        {/* CỘT 3: CHI TIẾT KHÁCH HÀNG (CỘT CỐ ĐỊNH SÁT PHẢI - INLINE COLUMN) */}
        {selectedCustomer && (
          <div className="lg:col-span-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-3.5 shadow-2xs space-y-4 relative">
            
            <button
              onClick={() => setSelectedCustomer(null)}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="pr-6">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">CHƯA CÓ MÃ</div>
              <div className="flex items-center justify-between mt-0.5">
                <h2 className="text-lg font-black text-slate-900 dark:text-white truncate">{selectedCustomer.customer}</h2>
              </div>
              <div className="mt-1">
                <span className="bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 text-[10px] font-bold px-2 py-0.5 rounded border border-amber-300/60">
                  + {selectedCustomer.statusText}
                </span>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/80 p-2.5 rounded-md border border-slate-100 dark:border-slate-700 space-y-1.5">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">HÀNH TRÌNH KHÁCH</div>

              <div className="grid grid-cols-5 gap-1 pt-1">
                <div className="h-1 bg-emerald-600 rounded-full"></div>
                <div className="h-1 bg-emerald-600 rounded-full"></div>
                <div className="h-1 bg-emerald-600 rounded-full"></div>
                <div className="h-1 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                <div className="h-1 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
              </div>

              <div className="grid grid-cols-5 text-[9px] pt-0.5 text-center">
                <span className="text-emerald-600 font-medium">Hẹn</span>
                <span className="text-emerald-600 font-medium">Tiếp đón</span>
                <span className="text-emerald-600 font-medium">Tư vấn</span>
                <span className="text-slate-400">Xếp ca</span>
                <span className="text-slate-400">Thực hiện</span>
              </div>

              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2">
                <strong className="text-slate-700 dark:text-slate-200">Hẹn:</strong> hẹn {selectedCustomer.appointmentTime || '02/10 10:00'}
              </p>
            </div>

            <div className="space-y-1.5">
              <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">CHUYỂN TRẠNG THÁI</div>
              <div className="flex flex-wrap gap-1">
                {['Xác nhận', 'Khách đã đến', 'Đã thực hiện', 'Không đến', 'Dời lịch'].map((st) => (
                  <button
                    key={st}
                    onClick={() => handleUpdateStatus(st)}
                    className={`px-2.5 py-1 font-medium rounded text-[11px] border transition-colors ${
                      selectedCustomer.statusText === st
                        ? 'bg-teal-700 text-white border-teal-700'
                        : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-100 border-slate-200 dark:border-slate-600'
                    }`}
                  >
                    {st}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-0.5">
              <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">DỊCH VỤ KHÁCH ĐẶT</div>
              <div className="font-bold text-sm text-slate-900 dark:text-white truncate">{selectedCustomer.service}</div>
              <div className="text-[11px] text-slate-400 italic">{selectedCustomer.price || 'chưa có giá trị'}</div>
            </div>

            <div className="space-y-2">
              <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">LỊCH HẸN</div>
              <div className="space-y-1 text-[11px]">
                <div className="flex justify-between"><span className="text-slate-500">Thời gian</span><span className="font-bold text-slate-800 dark:text-slate-200">{selectedCustomer.startTime} · {selectedCustomer.dayLabel}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Thời lượng</span><span className="font-bold text-slate-800 dark:text-slate-200">60 phút</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Chi nhánh</span><span className="font-bold text-slate-800 dark:text-slate-200">Trần Hưng Đạo Q1</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Điện thoại</span><span className="text-slate-400">{selectedCustomer.phone || '– thiếu SĐT'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Telesale</span><span className="font-bold text-slate-800 dark:text-slate-200">{selectedCustomer.telesale || 'Bửu Châu'}</span></div>
              </div>
              <div className="flex gap-1.5 pt-1">
                <button
                  onClick={() => message.info(`Đang kết nối cuộc gọi tới ${selectedCustomer.phone}`)}
                  className="flex-1 py-1 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 border border-slate-200 dark:border-slate-600 rounded text-[11px] font-semibold flex items-center justify-center gap-1"
                >
                  <Phone className="w-3 h-3 text-slate-500" /> Gọi điện
                </button>
                <button
                  onClick={() => message.info(`Đang mở nhắn Zalo cho ${selectedCustomer.customer}`)}
                  className="flex-1 py-1 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 border border-slate-200 dark:border-slate-600 rounded text-[11px] font-semibold flex items-center justify-center gap-1"
                >
                  <MessageSquare className="w-3 h-3 text-slate-500" /> Nhắn Zalo
                </button>
              </div>
            </div>

            <div className="p-2.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-md text-[11px] text-amber-900 dark:text-amber-200 space-y-1">
              <div className="font-bold">Ghi chú tư vấn.</div>
              <p className="leading-relaxed">
                {selectedCustomer.note || 'Hồ sơ khách hàng cập nhật trực tiếp từ hệ thống quản lý lịch hẹn.'}
              </p>
            </div>
          </div>
        )}

      </div>

      {/* Drawer Đặt Lịch Nhanh Form */}
      <Drawer
        title={<span className="font-bold text-slate-900 dark:text-white">Đặt lịch nhanh</span>}
        placement="right"
        onClose={() => setIsQuickBookingOpen(false)}
        open={isQuickBookingOpen}
        width={360}
        className="dark:[&_.ant-drawer-content]:!bg-slate-800 dark:[&_.ant-drawer-header]:!bg-slate-800 dark:[&_.ant-drawer-header]:!border-slate-700 dark:[&_.ant-drawer-close]:!text-slate-400 dark:[&_.ant-drawer-title]:!text-white"
      >
        <div className="space-y-3 text-xs">
          <div>
            <label className="block font-bold text-slate-600 dark:text-slate-300 mb-1">TÊN KHÁCH HÀNG</label>
            <input
              type="text"
              value={newBooking.customer}
              onChange={(e) => setNewBooking((prev) => ({ ...prev, customer: e.target.value }))}
              placeholder="Nguyễn Văn A"
              className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-800 dark:text-slate-100"
            />
          </div>
          <div>
            <label className="block font-bold text-slate-600 dark:text-slate-300 mb-1">SỐ ĐIỆN THOẠI</label>
            <input
              type="text"
              value={newBooking.phone}
              onChange={(e) => setNewBooking((prev) => ({ ...prev, phone: e.target.value }))}
              placeholder="0901234567"
              className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-800 dark:text-slate-100"
            />
          </div>
          <div>
            <label className="block font-bold text-slate-600 dark:text-slate-300 mb-1">DỊCH VỤ</label>
            <input
              type="text"
              value={newBooking.service}
              onChange={(e) => setNewBooking((prev) => ({ ...prev, service: e.target.value }))}
              placeholder="Cấy mỡ hai bên má"
              className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-800 dark:text-slate-100"
            />
          </div>
          <div>
            <label className="block font-bold text-slate-600 dark:text-slate-300 mb-1">PHÒNG / KHU VỰC</label>
            <select
              value={newBooking.roomIndex}
              onChange={(e) => setNewBooking((prev) => ({ ...prev, roomIndex: Number(e.target.value) }))}
              className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-800 dark:text-slate-100"
            >
              {roomColumns.map((r, idx) => (
                <option key={r.id} value={idx}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-bold text-slate-600 dark:text-slate-300 mb-1">KHUNG GIỜ</label>
            <select
              value={newBooking.startTime}
              onChange={(e) => setNewBooking((prev) => ({ ...prev, startTime: e.target.value }))}
              className="w-full px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-800 dark:text-slate-100"
            >
              {timeSlots.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleCreateQuickBooking}
            className="w-full py-2 bg-teal-800 hover:bg-teal-900 text-white font-bold rounded mt-2 transition-colors"
          >
            Lưu đặt lịch
          </button>
        </div>
      </Drawer>

    </div>
  );
}